<template>
    <div class="relative inline-flex">
        <button 
            @click="toggleDropdown"
            type="button" 
            :class="triggerClasses"
            :disabled="disabled"
            :aria-expanded="isOpen"
            :aria-label="triggerText || 'Dropdown menu'">
            <span v-if="triggerText" class="hidden sm:inline-block">{{ triggerText }}</span>
            <svg v-if="triggerIcon" class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
            </svg>
        </button>

        <div 
            v-show="isOpen"
            :class="dropdownClasses"
            role="menu" 
            aria-orientation="vertical">
            <div class="p-1">
                <button 
                    v-for="option in options"
                    :key="option.id"
                    @click="handleOptionClick(option)"
                    type="button" 
                    :class="getOptionClasses(option)"
                    :disabled="option.disabled">
                    <img v-if="option.icon == 'lightroom'" src="@/assets/images/icons/lightroom.svg" class="size-4" alt="Copy to Lightroom">
                    <img v-if="option.icon == 'captureone'" src="@/assets/images/icons/captureone.svg" class="size-4" alt="Copy to Capture One">
                    <TrashIcon v-if="option.icon == 'delete'" class="size-4" />
                    {{ option.label }}
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onUnmounted } from 'vue';
import type { DropdownProps, DropdownEmits, DropdownOption } from '@/types/DropdownType';
import { TrashIcon } from '@heroicons/vue/24/outline';

export default defineComponent({
    name: 'DropdownMenu',
    
    components: {
        TrashIcon
    },
    
    props: {
        options: {
            type: Array as () => DropdownOption[],
            required: true
        },
        triggerText: {
            type: String,
            default: 'More'
        },
        triggerIcon: {
            type: String,
            default: 'more'
        },
        placement: {
            type: String as () => 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left',
            default: 'bottom-right'
        },
        size: {
            type: String as () => 'sm' | 'md' | 'lg',
            default: 'md'
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    emits: ['action', 'open', 'close'],

    setup(props: DropdownProps, { emit }: { emit: DropdownEmits }) {
        const isOpen = ref(false);

        const triggerClasses = computed(() => {
            const baseClasses = 'inline-flex justify-center items-center gap-x-1 rounded-lg border border-gray-200 bg-white text-xs text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-gray-100';
            
            const sizeClasses: Record<string, string> = {
                sm: 'size-7 sm:w-auto sm:h-auto sm:p-1.5 sm:ps-3',
                md: 'size-7 sm:w-auto sm:h-auto sm:p-1.5 sm:ps-3',
                lg: 'size-8 sm:w-auto sm:h-auto sm:p-2 sm:ps-4'
            };

            return `${baseClasses} ${sizeClasses[props.size || 'md']}`;
        });

        const dropdownClasses = computed(() => {
            const baseClasses = 'absolute top-full mt-1 w-60 transition-all duration-200 z-10 bg-white rounded-xl shadow-xl';
            
            const placementClasses: Record<string, string> = {
                'bottom-right': 'right-0',
                'bottom-left': 'left-0',
                'top-right': 'right-0 bottom-full mb-1',
                'top-left': 'left-0 bottom-full mb-1'
            };

            return `${baseClasses} ${placementClasses[props.placement || 'bottom-right']}`;
        });

        const getOptionClasses = (option: DropdownOption) => {
            const baseClasses = 'w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-gray-100';
            
            const variantClasses = {
                default: 'text-gray-800 hover:bg-gray-100',
                danger: 'text-red-600 hover:bg-gray-100'
            };

            return `${baseClasses} ${variantClasses[option.variant || 'default']}`;
        };

        const toggleDropdown = () => {
            if (props.disabled) return;
            
            isOpen.value = !isOpen.value;
            
            if (isOpen.value) {
                emit('open');
            } else {
                emit('close');
            }
        };

        const closeDropdown = () => {
            if (isOpen.value) {
                isOpen.value = false;
                emit('close');
            }
        };

        const handleOptionClick = (option: DropdownOption) => {
            if (option.disabled) return;
            
            closeDropdown();
            emit('action', option.action, option);
        };

        const handleClickOutside = (event: Event) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.relative.inline-flex')) {
                closeDropdown();
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        onUnmounted(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            isOpen,
            triggerClasses,
            dropdownClasses,
            getOptionClasses,
            toggleDropdown,
            handleOptionClick
        };
    }
});
</script>

<style scoped>
/* Додаткові стилі можна додати тут, якщо потрібно */
</style>
